import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { friendlyError } from '@/lib/errorMessage';
import {
  LoginChallengeError,
  parseLoginChallenge,
  type LoginChallenge,
  type LoginChallengeResponseData,
} from '@/lib/loginChallenge';
import type { WkLoginProof, WkSignResult, SSPResponse } from '@/types/auth';
import apiClient from '@/api/client';

/**
 * SSP Wallet Extension API
 * Exposed via window.ssp with a request(method, params) pattern
 * Based on ssp-wallet/examples/ssp-auth-demo
 */
interface SSPWalletProvider {
  request: <T = unknown>(method: string, params?: object) => Promise<SSPResponse<T>>;
  /** Advertised features (see lib/walletCapabilities); absent on wallets ≤ 2.1.3. */
  capabilities?: readonly string[];
}

declare global {
  interface Window {
    ssp?: SSPWalletProvider;
  }
}

interface WkSignParams {
  message: string;
  authMode: 1 | 2; // 1 = wallet only, 2 = wallet + key co-sign
  origin: string;
  siteName?: string;
  description?: string;
  iconUrl?: string;
}

interface UseSSPWalletReturn {
  isInstalled: boolean;
  isLoading: boolean;
  error: string | null;
  wkSign: (description?: string) => Promise<WkLoginProof | null>;
  syncEnterpriseNonces: () => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

interface ChallengeResponse {
  status: string;
  data: LoginChallengeResponseData;
}

/**
 * Fetch a login challenge (message + EL-2 challengeToken) from the backend.
 * The result is only ever held in this call chain's memory.
 */
async function fetchChallenge(): Promise<LoginChallenge> {
  const response = await apiClient.get<ChallengeResponse>('/auth/challenge');
  return parseLoginChallenge(response.data.data, window.location.origin);
}

// Module-level detection — runs once, no flicker on tab switch
let walletDetected = typeof window !== 'undefined' && typeof window.ssp !== 'undefined';
let detectionDone = walletDetected;

const detectionListeners = new Set<() => void>();

function notifyListeners() {
  for (const fn of detectionListeners) fn();
}

// Deferred checks for extensions that inject after initial load
if (typeof window !== 'undefined' && !walletDetected) {
  const recheck = () => {
    if (!walletDetected && typeof window.ssp !== 'undefined') {
      walletDetected = true;
      detectionDone = true;
      notifyListeners();
    }
  };
  setTimeout(() => {
    recheck();
    detectionDone = true;
    notifyListeners();
  }, 1000);
  window.addEventListener('load', recheck, { once: true });
}

export function useSSPWallet(): UseSSPWalletReturn {
  const { t } = useTranslation();
  const [isInstalled, setIsInstalled] = useState(walletDetected);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (detectionDone) {
      setIsInstalled(walletDetected);
      return;
    }
    const listener = () => {
      setIsInstalled(walletDetected);
    };
    detectionListeners.add(listener);
    return () => {
      detectionListeners.delete(listener);
    };
  }, []);

  /**
   * Request WK Identity signature (wallet + key co-sign)
   * This is the primary authentication method for SSP Enterprise
   */
  const wkSign = useCallback(
    async (description?: string): Promise<WkLoginProof | null> => {
      if (typeof window.ssp === 'undefined') {
        setError(t('errors.walletNotDetected'));
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Fetch challenge from backend. The challengeToken stays in this
        // closure and goes straight back to the server with the signatures.
        const challenge = await fetchChallenge();

        // Build request params (matching ssp-auth-demo exactly). The server's
        // message is passed through unchanged — the devices verify its grammar
        // and (v2) the origin embedded in it.
        const params: WkSignParams = {
          message: challenge.message,
          authMode: 2, // Wallet + Key co-sign (required for enterprise)
          origin: window.location.origin,
          siteName: 'SSP Enterprise',
          description: description ?? 'Sign in to access SSP Enterprise',
          iconUrl: 'https://enterprise.sspwallet.com/ssp-logo-black-256x256.png',
        };

        const response = await window.ssp.request<WkSignResult>('wk_sign_message', params);

        if (response.status === 'SUCCESS') {
          const result = response.result;

          if (!result) {
            setError(t('errors.invalidWalletResponse'));
            return null;
          }

          // Validate the response (client-side validation)
          if (!result.walletSignature || !result.walletPubKey) {
            setError(t('errors.missingWalletSignature'));
            return null;
          }

          if (!result.witnessScript || !result.wkIdentity) {
            setError(t('errors.missingIdentityData'));
            return null;
          }

          // For two-factor mode (authMode 2), check key signature
          if (!result.keySignature || !result.keyPubKey) {
            setError(t('errors.missingKeySignature2fa'));
            return null;
          }

          // Validate public key format (66 hex chars for compressed)
          if (result.walletPubKey.length !== 66) {
            setError(t('errors.invalidPubKey'));
            return null;
          }

          if (result.keyPubKey.length !== 66) {
            setError(t('errors.invalidPubKey'));
            return null;
          }

          // Validate identity address format (bc1q for mainnet P2WSH, tb1q for testnet)
          if (!result.wkIdentity.startsWith('bc1q') && !result.wkIdentity.startsWith('tb1q')) {
            setError(t('errors.invalidIdentityAddress'));
            return null;
          }

          // The wallet must have signed exactly the challenge we were issued.
          if (result.message !== challenge.message) {
            setError(t('errors.challengeMessageMismatch'));
            return null;
          }

          const proof: WkLoginProof = {
            ...result,
            message: challenge.message,
            ...(challenge.challengeToken ? { challengeToken: challenge.challengeToken } : {}),
          };
          return proof;
        } else {
          // Error case
          const errorMessage =
            response.result ?? response.data ?? t('errors.authCancelledOrFailed');
          setError(typeof errorMessage === 'string' ? errorMessage : t('errors.authFailed'));
          return null;
        }
      } catch (err) {
        if (err instanceof LoginChallengeError) {
          setError(
            err.code === 'ORIGIN_MISMATCH'
              ? t('errors.challengeOriginMismatch')
              : t('errors.invalidLoginChallenge')
          );
          return null;
        }
        console.error('Authentication error:', err);
        const message = friendlyError(err, t, 'errors.generic');
        setError(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  /**
   * Trigger enterprise nonce replenishment in the wallet extension.
   * Returns true if sync succeeded, false otherwise.
   */
  const syncEnterpriseNonces = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (typeof window.ssp === 'undefined') {
      return { success: false, error: t('errors.walletNotDetected') };
    }
    try {
      const response = await window.ssp.request('enterprise_nonce_sync', {
        origin: window.location.origin,
        siteName: 'SSP Enterprise',
        iconUrl: 'https://enterprise.sspwallet.com/ssp-logo-black-256x256.png',
      });
      if (response.status === 'ERROR') {
        const errMsg =
          typeof response.result === 'string'
            ? response.result
            : (response.data ?? t('errors.nonceSyncFailed'));
        return { success: false, error: errMsg };
      }
      return { success: true };
    } catch {
      return { success: false, error: t('errors.nonceSyncFailed') };
    }
  }, [t]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isInstalled,
    isLoading,
    error,
    wkSign,
    syncEnterpriseNonces,
    clearError,
  };
}

export default useSSPWallet;
